// src/pages/Campaigns/SurveyGenie/SurveyDistribution.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Grid, Card, CardContent, CardActions,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Alert, Snackbar, CircularProgress,
    InputAdornment, Tooltip, FormControl, InputLabel,
    Select, MenuItem 
} from '@mui/material';
import {
    ArrowBack, Email, Sms, Link as LinkIcon, Code, QrCode,
    SmartToy, Send, ContentCopy, Download, Share, People, OpenInNew
} from '@mui/icons-material';
import { WarningAmber } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import axiosInstance from '../../../utils/axiosInstance';
import { DISTRIBUTION_CHANNEL, DISTRIBUTION_CHANNEL_LABELS } from './surveyEnums';
import { CHATBOT_UI } from '../../../config';

const SurveyDistribution = () => {
    const { surveyId, chatbotId } = useParams(); // Added chatbotId
    const navigate = useNavigate();
    
    const [survey, setSurvey] = useState(null);
    const [distributions, setDistributions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Dialog states
    const [emailDialog, setEmailDialog] = useState(false);
    const [smsDialog, setSmsDialog] = useState(false);
    const [linkDialog, setLinkDialog] = useState(false);
    const [embedDialog, setEmbedDialog] = useState(false);
    const [qrDialog, setQrDialog] = useState(false);
    const [qrLinkType, setQrLinkType] = useState('website');
    const [recipientsDialog, setRecipientsDialog] = useState(false);
    const [websiteConfig, setWebsiteConfig] = useState(null);
    
    // Form states
    const [emailForm, setEmailForm] = useState({
        subject: '',
        message: '',
        recipients: ''
    });
    const [smsForm, setSmsForm] = useState({
        message: '',
        recipients: ''
    });
    
    // Kept for future logic if needed, though currently unused in createDistribution call below
    const [linkSettings] = useState({
        allowMultiple: false,
        expiresAt: ''
    });

    const [embedSettings, setEmbedSettings] = useState({
        type: 'iframe',
        width: '100%',
        height: '600px'
    });
    const [selectedDistribution, setSelectedDistribution] = useState(null);

    const fetchSurveyAndDistributions = useCallback(async () => {
        try {
            setLoading(true);
            const [surveyRes, distRes, configRes] = await Promise.all([ // Added config fetch
                axiosInstance.get('/api/surveys/details', { params: { survey_id: surveyId } }),
                axiosInstance.get('/api/surveys/distributions', { params: { survey_id: surveyId } }),
                axiosInstance.get('/api/website-configuration', { params: { chatbot_id: chatbotId } }) // Fetch config
            ]);
            
            if (surveyRes.data.success) setSurvey(surveyRes.data.data);
            if (distRes.data.success) setDistributions(distRes.data.data || []);
            
            // Handle Config Data (Assuming API returns structure like {data: [{website_address: ...}]})
            if (configRes.data.success) {
                const configData = JSON.parse(configRes.data.data);
                setWebsiteConfig(configData && configData.length > 0 ? configData[0] : null);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
            showSnackbar('Error loading distribution data', 'error');
        } finally {
            setLoading(false);
        }
    }, [surveyId, chatbotId]);

    useEffect(() => {
        fetchSurveyAndDistributions();
    }, [fetchSurveyAndDistributions]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // FIX: Use survey_uuid from backend response
    const getSurveyUrl = () => {
        if (!survey?.survey_uuid) return '';
        // Use window.location.origin for the base URL, assuming public route handles /survey/:uuid
        // If your public route is different (e.g., /public/survey/:uuid), adjust here.
        return `${window.location.origin}/volunteer/availability/${survey.survey_uuid}`; 
        // Note: Check if you have a specific route for Surveys in App.js. 
        // Based on your App.js, you might not have a generic public survey route mapped yet 
        // except likely inside PublicSurveyForm. Assuming standard structure:
        // return `${window.location.origin}/survey/${survey.survey_uuid}`;
    };

    // Helper to check if website is configured
    const isWebsiteConfigured = () => {
        return websiteConfig && 
               websiteConfig.website_address && 
               !websiteConfig.website_address.includes("example.com");
    };

    const getPublicLink = () => {
         if (!survey?.survey_uuid) return 'Loading...';
         
         // 1. If website is configured (Client Website)
         if (isWebsiteConfigured()) {
             let baseUrl = websiteConfig.website_address;
             if (!baseUrl.startsWith('http')) baseUrl = 'https://' + baseUrl;
             baseUrl = baseUrl.replace(/\/$/, ""); 
             // Returns: https://client-site.com?klonit_survey=UUID
             return `${baseUrl}?klonit_survey=${survey.survey_uuid}`;
         }

         // 2. Fallback (Standalone Chatbot UI)
         // Returns: https://chatbot.klonit.ai?CHATBOT_ID=ID&klonit_survey=UUID
         let fallbackUrl = CHATBOT_UI;
         if (fallbackUrl.endsWith('/')) fallbackUrl = fallbackUrl.slice(0, -1);
         
         return `${fallbackUrl}?CHATBOT_ID=${chatbotId}&klonit_survey=${survey.survey_uuid}`;
    }

    const getChatbotLink = () => {
        if (!survey?.survey_uuid) return 'Loading...';
        
        let chatbotUrl = CHATBOT_UI;
        if (chatbotUrl.endsWith('/')) chatbotUrl = chatbotUrl.slice(0, -1);
        
        return `${chatbotUrl}?CHATBOT_ID=${chatbotId}&klonit_survey=${survey.survey_uuid}`;
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!');
    };

    const createDistribution = async (channel, settings = {}) => {
        try {
            const response = await axiosInstance.post('/api/surveys/distributions', {
                survey_id: surveyId,
                channel,
                settings
            });
            
            if (response.data.success) {
                showSnackbar('Distribution created successfully');
                fetchSurveyAndDistributions();
                return response.data.data;
            }
        } catch (error) {
            console.error('Error creating distribution:', error);
            showSnackbar(error.response?.data?.message || 'Error creating distribution', 'error');
        }
        return null;
    };

    const addRecipients = async (distributionId, recipients, channel) => {
        try {
            const response = await axiosInstance.post('/api/surveys/distributions/recipients', {
                distribution_id: distributionId,
                recipients: recipients.map(r => ({
                    email: channel === DISTRIBUTION_CHANNEL.EMAIL ? r : null,
                    phone: channel === DISTRIBUTION_CHANNEL.SMS ? r : null
                }))
            });
            
            if (response.data.success) {
                showSnackbar('Recipients added successfully');
                fetchSurveyAndDistributions();
            }
        } catch (error) {
            console.error('Error adding recipients:', error);
            showSnackbar(error.response?.data?.message || 'Error adding recipients', 'error');
        }
    };

    const handleSendEmail = async () => {
        const recipients = emailForm.recipients
            .split(/[,\n]/)
            .map(e => e.trim())
            .filter(e => e && e.includes('@'));
        
        if (recipients.length === 0) {
            showSnackbar('Please enter valid email addresses', 'error');
            return;
        }
        
        let distribution = distributions.find(d => d.channel === DISTRIBUTION_CHANNEL.EMAIL);
        if (!distribution) {
            distribution = await createDistribution(DISTRIBUTION_CHANNEL.EMAIL, {
                subject: emailForm.subject,
                message: emailForm.message
            });
        }
        
        if (distribution) {
            await addRecipients(distribution.id, recipients, DISTRIBUTION_CHANNEL.EMAIL);
            setEmailDialog(false);
            setEmailForm({ subject: '', message: '', recipients: '' });
        }
    };

    const handleSendSMS = async () => {
        const recipients = smsForm.recipients
            .split(/[,\n]/)
            .map(p => p.trim().replace(/\D/g, ''))
            .filter(p => p.length >= 10);
        
        if (recipients.length === 0) {
            showSnackbar('Please enter valid phone numbers', 'error');
            return;
        }
        
        let distribution = distributions.find(d => d.channel === DISTRIBUTION_CHANNEL.SMS);
        if (!distribution) {
            distribution = await createDistribution(DISTRIBUTION_CHANNEL.SMS, {
                message: smsForm.message
            });
        }
        
        if (distribution) {
            await addRecipients(distribution.id, recipients, DISTRIBUTION_CHANNEL.SMS);
            setSmsDialog(false);
            setSmsForm({ message: '', recipients: '' });
        }
    };

    const generateEmbedCode = () => {
        const url = getPublicLink();
        switch (embedSettings.type) {
            case 'iframe':
                return `<iframe src="${url}" width="${embedSettings.width}" height="${embedSettings.height}" frameborder="0"></iframe>`;
            case 'popup':
                return `<script>function openSurvey(){window.open('${url}','survey','width=600,height=700,scrollbars=yes');}</script><button onclick="openSurvey()">Take Survey</button>`;
            case 'slider':
                return `<script src="${window.location.origin}/survey-widget.js"></script><script>SurveyWidget.init({surveyUrl:'${url}',position:'right',triggerText:'Take Survey'});</script>`;
            default:
                return '';
        }
    };

    const getChannelIcon = (channel) => {
        switch (channel) {
            case DISTRIBUTION_CHANNEL.EMAIL: return <Email />;
            case DISTRIBUTION_CHANNEL.SMS: return <Sms />;
            case DISTRIBUTION_CHANNEL.LINK: return <LinkIcon />;
            case DISTRIBUTION_CHANNEL.EMBED: return <Code />;
            case DISTRIBUTION_CHANNEL.QR_CODE: return <QrCode />;
            case DISTRIBUTION_CHANNEL.CHATBOT: return <SmartToy />;
            default: return <Share />;
        }
    };

    const getDistributionStats = (distribution) => {
        const recipients = distribution.recipients || [];
        const sent = recipients.filter(r => r.sent_at).length;
        const opened = recipients.filter(r => r.opened_at).length;
        const completed = recipients.filter(r => r.completed_at).length;
        return { total: recipients.length, sent, opened, completed };
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={3}>
                {/* FIX: Correct Navigation Path */}
                <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="h5" fontWeight="bold">
                        Distribute Survey
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {survey?.title}
                    </Typography>
                </Box>
                <Chip 
                    label={survey?.status} 
                    color={survey?.status === 'ACTIVE' ? 'success' : 'default'}
                    sx={{ mr: 2 }}
                />
            </Box>

            {!isWebsiteConfigured() && !loading && (
                <Alert 
                    severity="warning" 
                    icon={<WarningAmber />}
                    action={
                        <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => navigate(`/chatbot/${chatbotId}/configuration`)}
                        >
                            Configure Now
                        </Button>
                    }
                    sx={{ mb: 3 }}
                >
                    <strong>Website not configured.</strong> To allow users to take surveys inside your chatbot on your website, please configure your Website Address. currently using public standalone link.
                </Alert>
            )}

            {survey?.status !== 'ACTIVE' && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    This survey is not active. Please publish it before distributing.
                </Alert>
            )}

            {/* Quick Share Section - TWO COLUMN LAYOUT */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    Quick Share Links
                </Typography>
                
                <Grid container spacing={3}>
                    {/* Left Column - Website/Public Link */}
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {isWebsiteConfigured() ? 'Website Link' : 'Public Survey Link'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                {isWebsiteConfigured() 
                                    ? 'Survey will open embedded in your website chatbot' 
                                    : 'Standalone survey link (using chatbot UI)'}
                            </Typography>
                            <TextField
                                fullWidth
                                value={getPublicLink()}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Copy Link">
                                                <IconButton onClick={() => copyToClipboard(getPublicLink())}>
                                                    <ContentCopy />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Open in New Tab">
                                                <IconButton onClick={() => window.open(getPublicLink(), '_blank')}>
                                                    <OpenInNew />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ mb: 2 }}
                            />
                            <Button 
                                variant="contained" 
                                startIcon={<QrCode />} 
                                onClick={() => {
                                    setQrLinkType('website');
                                    setQrDialog(true);
                                }}
                                fullWidth
                                size="large"
                            >
                                Generate QR Code
                            </Button>
                        </Box>
                    </Grid>

                    {/* Right Column - Chatbot Standalone Link */}
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Chatbot Standalone Link
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Direct link to survey in standalone chatbot interface
                            </Typography>
                            <TextField
                                fullWidth
                                value={getChatbotLink()}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Copy Link">
                                                <IconButton onClick={() => copyToClipboard(getChatbotLink())}>
                                                    <ContentCopy />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Open in New Tab">
                                                <IconButton onClick={() => window.open(getChatbotLink(), '_blank')}>
                                                    <OpenInNew />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ mb: 2 }}
                            />
                            <Button 
                                variant="contained" 
                                startIcon={<QrCode />} 
                                onClick={() => {
                                    setQrLinkType('chatbot');
                                    setQrDialog(true);
                                }}
                                fullWidth
                                size="large"
                            >
                                Generate QR Code
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Distribution Channels */}
            <Typography variant="h6" gutterBottom>Distribution Channels</Typography>
            <Grid container spacing={3} mb={3}>
                {/* Email */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Email sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">Email</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Send survey invitations via email
                                    </Typography>
                                </Box>
                            </Box>
                            {distributions.filter(d => d.channel === DISTRIBUTION_CHANNEL.EMAIL).map(dist => {
                                const stats = getDistributionStats(dist);
                                return (
                                    <Box key={dist.id} mb={1}>
                                        <Typography variant="body2">
                                            {stats.total} recipients • {stats.sent} sent • {stats.completed} completed
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </CardContent>
                        <CardActions>
                            <Button 
                                startIcon={<Send />} 
                                onClick={() => navigate(`/chatbot/${chatbotId}/surveys/distribute/email/${surveyId}`)}
                                disabled={survey?.status !== 'ACTIVE'}
                            >
                                Send Emails
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* SMS */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Sms sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">SMS</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Send survey links via text message
                                    </Typography>
                                </Box>
                            </Box>
                            {distributions.filter(d => d.channel === DISTRIBUTION_CHANNEL.SMS).map(dist => {
                                const stats = getDistributionStats(dist);
                                return (
                                    <Box key={dist.id} mb={1}>
                                        <Typography variant="body2">
                                            {stats.total} recipients • {stats.sent} sent • {stats.completed} completed
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </CardContent>
                        <CardActions>
                            <Button 
                                startIcon={<Send />} 
                                onClick={() => navigate(`/chatbot/${chatbotId}/surveys/distribute/sms/${surveyId}`)}
                                disabled={survey?.status !== 'ACTIVE'}
                            >
                                Send SMS
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* Embed */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Code sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">Embed</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Embed survey on your website
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                        <CardActions>
                            <Button 
                                startIcon={<Code />} 
                                onClick={() => setEmbedDialog(true)}
                            >
                                Get Embed Code
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>

            {/* Distribution History */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Distribution History</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Channel</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Recipients</TableCell>
                                <TableCell>Sent</TableCell>
                                <TableCell>Opened</TableCell>
                                <TableCell>Completed</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {distributions.map(dist => {
                                const stats = getDistributionStats(dist);
                                return (
                                    <TableRow key={dist.id}>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                {getChannelIcon(dist.channel)}
                                                <Typography sx={{ ml: 1 }}>
                                                    {DISTRIBUTION_CHANNEL_LABELS[dist.channel]}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {new Date(dist.created_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>{stats.total}</TableCell>
                                        <TableCell>{stats.sent}</TableCell>
                                        <TableCell>{stats.opened}</TableCell>
                                        <TableCell>{stats.completed}</TableCell>
                                        <TableCell>
                                            <Tooltip title="View Recipients">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedDistribution(dist);
                                                        setRecipientsDialog(true);
                                                    }}
                                                >
                                                    <People />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {distributions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography color="text.secondary">
                                            No distributions yet. Use the channels above to start sharing your survey.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Dialogs... (Email, SMS, Embed, QR, Recipients) - Kept mostly same but ensuring they use handler functions above */}
            {/* Email Dialog */}
            <Dialog open={emailDialog} onClose={() => setEmailDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Send Email Invitations</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Subject"
                        value={emailForm.subject}
                        onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                        placeholder="You're invited to take our survey"
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Message"
                        value={emailForm.message}
                        onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                        placeholder="We'd love to hear your feedback..."
                        multiline
                        rows={4}
                        margin="normal"
                        helperText="Use {{survey_link}} to insert the survey link"
                    />
                    <TextField
                        fullWidth
                        label="Email Addresses"
                        value={emailForm.recipients}
                        onChange={(e) => setEmailForm({ ...emailForm, recipients: e.target.value })}
                        placeholder="Enter email addresses (one per line or comma-separated)"
                        multiline
                        rows={6}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialog(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<Send />} onClick={handleSendEmail}>
                        Send Invitations
                    </Button>
                </DialogActions>
            </Dialog>

            {/* SMS Dialog */}
            <Dialog open={smsDialog} onClose={() => setSmsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Send SMS Invitations</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Message"
                        value={smsForm.message}
                        onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })}
                        placeholder="Take our quick survey: {{survey_link}}"
                        multiline
                        rows={3}
                        margin="normal"
                        helperText="Keep it short! Use {{survey_link}} to insert the survey link"
                    />
                    <TextField
                        fullWidth
                        label="Phone Numbers"
                        value={smsForm.recipients}
                        onChange={(e) => setSmsForm({ ...smsForm, recipients: e.target.value })}
                        placeholder="Enter phone numbers (one per line or comma-separated)"
                        multiline
                        rows={6}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSmsDialog(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<Send />} onClick={handleSendSMS}>
                        Send SMS
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Embed Dialog */}
            <Dialog open={embedDialog} onClose={() => setEmbedDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Embed Survey</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Embed Type</InputLabel>
                        <Select
                            value={embedSettings.type}
                            label="Embed Type"
                            onChange={(e) => setEmbedSettings({ ...embedSettings, type: e.target.value })}
                        >
                            <MenuItem value="iframe">iFrame (Inline)</MenuItem>
                            <MenuItem value="popup">Popup Window</MenuItem>
                            <MenuItem value="slider">Slide-in Widget</MenuItem>
                        </Select>
                    </FormControl>
                    {embedSettings.type === 'iframe' && (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Width"
                                    value={embedSettings.width}
                                    onChange={(e) => setEmbedSettings({ ...embedSettings, width: e.target.value })}
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Height"
                                    value={embedSettings.height}
                                    onChange={(e) => setEmbedSettings({ ...embedSettings, height: e.target.value })}
                                    margin="normal"
                                />
                            </Grid>
                        </Grid>
                    )}
                    <TextField
                        fullWidth
                        label="Embed Code"
                        value={generateEmbedCode()}
                        multiline
                        rows={6}
                        margin="normal"
                        InputProps={{ readOnly: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmbedDialog(false)}>Close</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<ContentCopy />} 
                        onClick={() => {
                            copyToClipboard(generateEmbedCode());
                            setEmbedDialog(false);
                        }}
                    >
                        Copy Code
                    </Button>
                </DialogActions>
            </Dialog>

            {/* QR Code Dialog */}
            <Dialog open={qrDialog} onClose={() => setQrDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
                QR Code - {qrLinkType === 'website' 
                    ? (isWebsiteConfigured() ? 'Website Link' : 'Public Link') 
                    : 'Chatbot Standalone'}
            </DialogTitle>
            <DialogContent>
                {/* QR Code Display */}
                <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                    <QRCodeSVG 
                        id="qr-code"
                        value={qrLinkType === 'website' ? getPublicLink() : getChatbotLink()} 
                        size={256}
                        level="H"
                        includeMargin
                    />
                    <Typography variant="body2" color="text.secondary" mt={2}>
                        Scan to take the survey
                    </Typography>
                    <Typography variant="caption" color="text.secondary" mt={1}>
                        {qrLinkType === 'website' 
                            ? (isWebsiteConfigured() ? 'Website Embedded' : 'Public Survey') 
                            : 'Chatbot Standalone'}
                    </Typography>
                </Box>
            </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQrDialog(false)}>Close</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<Download />}
                        onClick={() => {
                            const svg = document.querySelector('#qr-code'); // Select by ID
                            if (svg) {
                                const svgData = new XMLSerializer().serializeToString(svg);
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                const img = new Image();
                                img.onload = () => {
                                    canvas.width = img.width;
                                    canvas.height = img.height;
                                    ctx.drawImage(img, 0, 0);
                                    const pngFile = canvas.toDataURL('image/png');
                                    const downloadLink = document.createElement('a');
                                    downloadLink.download = `survey-qr-${qrLinkType}-${survey?.survey_uuid}.png`;
                                    downloadLink.href = pngFile;
                                    downloadLink.click();
                                };
                                img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                            }
                        }}
                    >
                        Download PNG
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Recipients Dialog */}
            <Dialog 
                open={recipientsDialog} 
                onClose={() => setRecipientsDialog(false)} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>
                    Recipients - {DISTRIBUTION_CHANNEL_LABELS[selectedDistribution?.channel]}
                </DialogTitle>
                <DialogContent>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>Sent</TableCell>
                                    <TableCell>Opened</TableCell>
                                    <TableCell>Completed</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(selectedDistribution?.recipients || []).map((recipient, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            {recipient.email || recipient.phone}
                                        </TableCell>
                                        <TableCell>
                                            {recipient.sent_at 
                                                ? new Date(recipient.sent_at).toLocaleString() 
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {recipient.opened_at 
                                                ? new Date(recipient.opened_at).toLocaleString() 
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {recipient.completed_at 
                                                ? new Date(recipient.completed_at).toLocaleString() 
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {recipient.completed_at ? (
                                                <Chip label="Completed" color="success" size="small" />
                                            ) : recipient.opened_at ? (
                                                <Chip label="Opened" color="info" size="small" />
                                            ) : recipient.sent_at ? (
                                                <Chip label="Sent" color="default" size="small" />
                                            ) : (
                                                <Chip label="Pending" color="warning" size="small" />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRecipientsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SurveyDistribution;